/**
 * Defensive wrapper around the global Hotjar function (`window.hj`).
 *
 * Hotjar's script is loaded asynchronously via GTM, so `window.hj` is often
 * still undefined when an event fires — especially on landing pages, where
 * the event fires on mount while GTM is still loading. Instead of dropping
 * such events, we install Hotjar's official stub queue: calls collect in
 * `window.hj.q` and the real script drains the queue once it loads.
 *
 * If Hotjar never loads (ad-blocker, marketing cookies declined), the queue
 * is simply never sent.
 *
 * @typedef {(...args: unknown[]) => void} HotjarFn
 * @typedef {HotjarFn & { q?: unknown[] }} HotjarGlobal
 */

/** @returns {HotjarGlobal | undefined} */
function getOrInstallHj() {
  if (typeof window === 'undefined') return undefined;
  if (!window.hj) {
    window.hj = function (...args) {
      (window.hj.q = window.hj.q || []).push(args);
    };
  }
  return /** @type {HotjarGlobal} */ (window.hj);
}

/**
 * Fire a Hotjar custom event used for session-capture targeting.
 * Queued until Hotjar's script loads when fired early.
 *
 * @param {string} eventName
 */
export function hotjarEvent(eventName) {
  const hj = getOrInstallHj();
  if (!hj) return;
  hj('event', eventName);
}
